import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InpatientBillView } from '@wodi/shared';
import {
  Button,
  LoadingBlock,
  Notice,
  PrintFooter,
  PrintHeader,
  PrintLines,
  PrintSheet,
  PrintTable,
  printSheet,
} from '@wodi/ui';
import { useEffect, useRef } from 'react';

import {
  inpatientBillQuery,
  markInpatientBillPrinted,
  stayBillingKeys,
} from '../api/inpatient-billing.ts';
import { money } from '../billing/format.ts';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { balanceWords } from './words.ts';

// The printed stay bill on A4 (docs/plans/phase2-inpatient-billing.md §6): the lines as they
// read when it was issued, the deposits and the balance. Reprints say "Copy"; prints are
// audited. The words live in one keyed table for a later reviewed Kiswahili version.

export const STAY_BILL_TEXT = {
  en: {
    interim: 'Interim bill',
    final: 'Final bill',
    copy: (n: number) => `Copy ${String(n)}`,
    billNo: 'Bill no.',
    issued: 'Issued',
    patient: 'Patient',
    patientNo: 'Patient no.',
    visitNo: 'Stay visit no.',
    admitted: 'Admitted',
    discharged: 'Discharged',
    stillAdmitted: 'Still admitted',
    payer: 'Payer',
    item: 'Item',
    date: 'Date',
    qty: 'Qty',
    amount: 'Amount',
    payerPays: 'Payer pays',
    patientPays: 'Patient pays',
    notPriced: 'Not priced',
    charged: 'Charged',
    waived: 'Waived or discounted',
    credited: 'Credited',
    paid: 'Paid, including deposits applied',
    depositsReceived: 'Deposits received',
    depositsApplied: 'Deposits applied',
    depositsRefunded: 'Deposits refunded',
    depositHeld: 'Deposit held',
    interimNote: 'An interim bill is a statement of the stay so far. It is not the final bill.',
    notSigned: 'The tax invoice is not yet signed through eTIMS.',
    mflCode: (code: string) => `MFL ${code}`,
  },
} as const;

const T = STAY_BILL_TEXT.en;

export function StayBillSheet({
  bill,
  sheetRef,
}: {
  bill: InpatientBillView;
  sheetRef?: React.Ref<HTMLElement>;
}) {
  const copy = bill.printCount > 0 ? bill.printCount + 1 : null;
  const title = bill.kind === 'final' ? T.final : T.interim;
  const t = bill.totals;
  return (
    <PrintSheet paper="a4" label={title} {...(sheetRef ? { ref: sheetRef } : {})}>
      <PrintHeader
        facility={bill.facility.name}
        facilityLines={bill.facility.mflCode === null ? [] : [T.mflCode(bill.facility.mflCode)]}
        title={copy === null ? title : `${title}, ${T.copy(copy)}`}
      />
      <PrintLines
        items={[
          { label: T.billNo, value: bill.number, identifier: true },
          { label: T.issued, value: `${formatInstant(bill.issuedAt)}, ${bill.issuedBy.name}` },
          { label: T.patient, value: bill.patient.name },
          { label: T.patientNo, value: bill.patient.patientNumber, identifier: true },
          { label: T.visitNo, value: bill.visitNumber, identifier: true },
          { label: T.admitted, value: formatInstant(bill.admittedAt) },
          {
            label: T.discharged,
            value: bill.dischargedAt === null ? T.stillAdmitted : formatInstant(bill.dischargedAt),
          },
          { label: T.payer, value: bill.payer },
        ]}
      />
      <PrintTable>
        <thead>
          <tr>
            <th>{T.item}</th>
            <th>{T.date}</th>
            <th data-numeric="">{T.qty}</th>
            <th data-numeric="">{T.amount}</th>
            <th data-numeric="">{T.payerPays}</th>
            <th data-numeric="">{T.patientPays}</th>
          </tr>
        </thead>
        <tbody>
          {bill.lines.map((line) => (
            <tr key={line.chargeId}>
              <td>
                {line.description}
                {line.note === null ? null : ` (${line.note})`}
              </td>
              <td>{formatInstant(line.occurredAt)}</td>
              <td data-numeric="">{line.quantity}</td>
              <td data-numeric="">
                {line.state === 'not_priced' ? T.notPriced : money(line.total)}
              </td>
              <td data-numeric="">{money(line.payerAmount)}</td>
              <td data-numeric="">{money(line.patientAmount)}</td>
            </tr>
          ))}
        </tbody>
      </PrintTable>
      <PrintLines
        items={[
          { label: T.charged, value: money(t.charged) },
          { label: T.payerPays, value: money(t.payerAmount) },
          { label: T.patientPays, value: money(t.patientAmount) },
          ...(t.adjusted.cents === '0' ? [] : [{ label: T.waived, value: money(t.adjusted) }]),
          ...(t.credited.cents === '0' ? [] : [{ label: T.credited, value: money(t.credited) }]),
          { label: T.paid, value: money(t.paid) },
          { label: T.depositsReceived, value: money(t.depositsReceived) },
          ...(t.depositsApplied.cents === '0'
            ? []
            : [{ label: T.depositsApplied, value: money(t.depositsApplied) }]),
          ...(t.depositsRefunded.cents === '0'
            ? []
            : [{ label: T.depositsRefunded, value: money(t.depositsRefunded) }]),
          { label: T.depositHeld, value: money(t.depositHeld) },
          { label: balanceWords(t).label, value: balanceWords(t).amount },
        ]}
      />
      {bill.kind === 'interim' ? <PrintFooter>{T.interimNote}</PrintFooter> : null}
      <PrintFooter>{T.notSigned}</PrintFooter>
    </PrintSheet>
  );
}

/**
 * Loads a stay bill and prints it on A4, then records the print (audited; later prints say
 * "Copy"). With `auto`, the print dialog opens as soon as it is ready, as after issuing.
 */
export function StayBillPrinter({ billId, auto }: { billId: string; auto: boolean }) {
  const queryClient = useQueryClient();
  const bill = useQuery(inpatientBillQuery(billId));
  const sheet = useRef<HTMLElement>(null);
  const printed = useMutation({
    mutationFn: () => markInpatientBillPrinted(billId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: stayBillingKeys.bill(billId) });
    },
  });
  const started = useRef(false);
  const print = () => {
    if (sheet.current === null) return;
    printSheet(sheet.current);
    printed.mutate();
  };
  useEffect(() => {
    if (auto && bill.isSuccess && !started.current && sheet.current !== null) {
      started.current = true;
      printSheet(sheet.current);
      printed.mutate();
    }
  }, [auto, bill.isSuccess, printed]);

  if (bill.isPending) return <LoadingBlock label="Loading the bill" rows={2} />;
  if (bill.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void bill.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(bill.error, "The bill couldn't be loaded. Try again.")}
      </Notice>
    );
  return (
    <div>
      <Button dense onPress={print} isPending={printed.isPending} pendingLabel="Printing">
        {bill.data.printCount > 0
          ? `Print a copy of ${bill.data.number}`
          : `Print ${bill.data.number}`}
      </Button>
      <StayBillSheet bill={bill.data} sheetRef={sheet} />
    </div>
  );
}
